import { createInertiaApp, router, type ResolvedComponent } from "@inertiajs/react";
import posthog from "posthog-js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

function initPostHog() {
  const phKey = document.querySelector<HTMLMetaElement>('meta[name="posthog-key"]')?.content;
  if (!phKey) return;

  const isDev = window.location.hostname === "localhost";

  posthog.init(phKey, {
    api_host: isDev ? "https://us.i.posthog.com" : "https://tapp.fotocerta.com",
    ui_host: "https://us.posthog.com",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    person_profiles: "always",
    session_recording: { maskAllInputs: false, maskInputOptions: { password: true } },
    enable_heatmaps: true,
    capture_exceptions: true,
    loaded: (ph) => {
      ph.capture("$pageview");
    },
  });

  return phKey;
}

void createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    return page;
  },

  setup({ el, App, props }) {
    const phKey = initPostHog();

    const page = props.initialPage;
    const currentUser = (page.props as Record<string, unknown>).current_user as
      | { id: number; email: string; age: number | null; gender: string | null }
      | null
      | undefined;

    if (phKey && currentUser) {
      posthog.identify(String(currentUser.id), {
        email: currentUser.email,
        age: currentUser.age,
        gender: currentUser.gender,
      });
    }

    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    );
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: true,
    },
    future: {
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.'
    );
  }
});

router.on("navigate", () => {
  posthog.capture("$pageview");

  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_title: document.title,
    });
  }
});
